import Vue from "vue";
import {
  MAP_DEFAULT_POSITION,
  MAP_STYLE_DEFAULT,
  MAP_DEFAULT_SELECTED_FAVORITE_INDEX,
} from "@/config/const";
import { firebase, db } from "@/firebase/firebaseConfig";
import AuthService from "@/services/authService";
import LocalDbService from "@/services/localDbService";
import {
  VesselMixin,
  PlotMixin,
  NavigationsMixin,
  PrivatePlotMixin,
} from "@/mixins";
import * as NumberUtil from "@/utils/NumberUtil";
import isString from "lodash/isString";

const COLLECTIONS = {
  ENTERPRISE_ACCOUNT_SETTINGS: "enterprise_account_settings",
  ENTERPRISE_ACCOUNTS: "enterprise_accounts",
  WEATHER: "weather",
  ENGINE_DATA: "engine_data",
  GKMG_PRIVATE_PLOTS: "gkmg_private_plots",
  GKMG_EQUIPMENT_BASE_STATIONS: "gkmg_equipment_base_stations",
  GKMG_PM_TASKS: "gkmg_pm_tasks",
};

class FireStoreService {
  /**
   * ユーザーの設定をFireStoreから取得しindexDBに設定する。
   * FireStoreにない場合はデフォルト値があるものだけをindexDBに設定する。
   */
  async setEnterpriseAccountSettingsToLocalDb() {
    let user = await AuthService.getUser();
    let settings =
      (await this.get(
        COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
        user.account_id,
      )) || {};
    if (settings) {
      // 初期表示位置
      if (settings.default_position) {
        let dp = settings.default_position;
        let defaultPosition = {
          center: [dp.center.longitude, dp.center.latitude],
          zoom: dp.zoom,
        };
        await LocalDbService.saveDefaultPosition(defaultPosition);
      } else {
        await LocalDbService.saveDefaultPosition(MAP_DEFAULT_POSITION);
      }
      // マップのスタイル
      if (settings.map_style) {
        await LocalDbService.saveMapStyle(settings.map_style);
      } else {
        await LocalDbService.saveMapStyle(MAP_STYLE_DEFAULT);
      }
      // 選択船
      if (settings.selected_vessels) {
        await LocalDbService.saveSelectedVessels(settings.selected_vessels);
      }
      // 船のフィルター設定
      if (settings.filter_vessels) {
        await LocalDbService.saveFilterVessels(settings.filter_vessels);
      } else {
        let vesselCategoryIds = VesselMixin.methods.getVesselCategoryIds();
        await LocalDbService.saveFilterVessels(vesselCategoryIds);
      }
      // プロットのフィルター設定
      if (settings.filter_plots) {
        await LocalDbService.saveFilterPlots(settings.filter_plots);
      } else {
        let plotCategoryIds = PlotMixin.methods.getPlotParentCategoryIds();
        await LocalDbService.saveFilterPlots(plotCategoryIds);
      }
      // プライベートプロットのフィルター設定
      if (settings.filter_private_plots) {
        await LocalDbService.saveFilterPrivatePlots(
          settings.filter_private_plots,
        );
      } else {
        let privatePlotCategoryIds =
          PrivatePlotMixin.methods.getPrivatePlotParentCategoryIds();
        await LocalDbService.saveFilterPrivatePlots(privatePlotCategoryIds);
      }
      // 航路、標識のフィルター設定
      if (settings.filter_navigations) {
        await LocalDbService.saveFilterNavigations(settings.filter_navigations);
      }
      // レーダーグリッドのフィルター設定
      if (settings.filter_radar_grid) {
        await LocalDbService.saveFilterRadarGrid(settings.filter_radar_grid);
      }
      // 自船の表示時時間
      if (settings.own_vessel_display_duration_time) {
        await LocalDbService.saveOwnVesselDisplayDurationTime(
          settings.own_vessel_display_duration_time,
        );
      } else {
        await LocalDbService.saveOwnVesselDisplayDurationTime(0);
      }
    }
  }

  /**
   * 初期表示位置をFireStoreに保存する
   * @param map 緯度軽度とZoom値
   */
  async saveDefaultPosition(map) {
    if (map === undefined) return;
    try {
      const user = await AuthService.getUser();
      const geoPoint = new firebase.firestore.GeoPoint(
        NumberUtil.toTrunc(map.center[1], 8), // 緯度
        NumberUtil.toTrunc(map.center[0], 8), // 経度
      );
      const data = {
        default_position: {
          center: geoPoint,
          zoom: NumberUtil.toTrunc(map.zoom, 2),
        },
      };
      await this.set(
        COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
        user.account_id,
        data,
        true,
      );
    } catch (e) {
      Vue.$log.error("Error method:saveDefaultPosition", `${e.message}`);
    }
  }

  /**
   * MapのStyleをFireStoreに保存する
   * @param style MapのStyle
   */
  async saveMapStyle(style) {
    if (style === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      map_style: style,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * 選択中の船舶をFireStoreに保存する
   * @param vesselIds 選択中の船舶ID一覧
   */
  async saveSelectedVessels(vesselIds) {
    if (vesselIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      selected_vessels: vesselIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * 船舶のフィルター対象カテゴリーIDDをFireStoreに保存する
   * @param targetCategoryIds 船舶のフィルター対象のカテゴリーID一覧
   */
  async saveFilterVessels(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_vessels: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * プロットのフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds プロットのフィルター対象のカテゴリーID（親）一覧
   */
  async saveFilterPlots(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_plots: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * プライベートプロットのフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds プライベートプロットのフィルター対象のカテゴリーID（親）一覧
   */
  async saveFilterPrivatePlots(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_private_plots: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * 航路、標識ののフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds 航路、標識のフィルター対象のカテゴリーID一覧
   */
  async saveFilterNavigations(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_navigations: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * レーダーグリッドのフィルター対象カテゴリIDをFireStoreに保存する
   * @param targetCategoryIds レーダーグリッドのフィルター対象のカテゴリーID一覧
   */
  async saveFilterRadarGrid(targetCategoryIds) {
    if (targetCategoryIds === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      filter_radar_grid: targetCategoryIds,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * 自船の表示時間をFireStoreに保存する
   * @param durationTime 自船の表示時間(分)
   */
  async saveOwnVesselDisplayDurationTime(durationTime) {
    if (durationTime === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      own_vessel_display_duration_time: durationTime,
    };
    await this.set(
      COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS,
      user.account_id,
      data,
      true,
    );
  }

  /**
   * 沖波ポイントをFireStoreに保存する
   * @param points 沖波ポイント一覧
   */
  async saveOffingWavePoints(points) {
    if (points === undefined) return;
    let user = await AuthService.getUser();
    let data = {
      polaris_offing_wave_points: points,
    };
    await this.set(COLLECTIONS.WEATHER, user.account_id, data, true);
  }

  /**
   * fireStoreのアカウントと船舶に紐づく機関情報設定を取得する.
   * @param {string} vesselId 船舶
   * @return {Object} アカウントと船舶に紐づく機関情報設定 {key: value}
   */
  async getEngineDataSetting(vesselId) {
    if (!vesselId) return {};
    const user = await AuthService.getUser();
    const ref = db
      .collection(COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS)
      .doc(user.account_id)
      .collection(COLLECTIONS.ENGINE_DATA)
      .doc(`v:${vesselId}`);

    return ref
      .get()
      .then((doc) => {
        if (!doc.exists) {
          return {}; // {}検知で空オブジェクトを返す
        } else {
          return doc.data();
        }
      })
      .catch((err) => {
        Vue.$log.error("Error setting document", err);
        return {};
      });
  }
  /**
   * fireStoreのアカウントと船舶に紐づく機関情報設定を保存する.
   * @param {string} vesselId 船舶
   * //@param {object} data 保存したいデータ {key: value}
   * @param {string} settingKey 保存したい設定のkey
   * @param {string} settingValue 保存したい設定の値
   */
  async setEngineDataSetting(vesselId, settingKey, settingValue) {
    if (!vesselId) return null;
    if (!settingKey) return null;
    const user = await AuthService.getUser();

    return db
      .collection(COLLECTIONS.ENTERPRISE_ACCOUNT_SETTINGS)
      .doc(user.account_id)
      .collection(COLLECTIONS.ENGINE_DATA)
      .doc(`v:${vesselId}`)
      .set({ [settingKey]: settingValue }, { merge: true })
      .catch((err) => {
        Vue.$log.error("Error setting document", err);
        return null;
      });
  }

  /**
   *  fireStoreの個別アカウント設定を取得する.
   */
  async getAccountSetting(settingDocName) {
    const accountId = await this.getFirebaseAccountId();
    if (accountId) {
      const docData = await this.get(
        `accounts/${accountId}/setting/`,
        settingDocName,
      );
      return docData;
    } else {
      return null;
    }
  }
  /**
   *  fireStoreの個別アカウント設定を保存する.
   */
  async setAccountSetting(settingDocName, data) {
    const accountId = await this.getFirebaseAccountId();
    if (accountId) {
      this.set(`accounts/${accountId}/setting/`, settingDocName, data, true);
    }
  }
  /**
   *  firebase認証で使うaccountIdを抽出する.
   */
  async getFirebaseAccountId() {
    const firebaseUser = firebase.auth().currentUser;
    if (firebaseUser) {
      const parsedToken = await firebaseUser.getIdTokenResult();
      return parsedToken.claims.accountId;
    } else {
      return null;
    }
  }

  /**
   * プライベートプロットの取得
   * 引数のenterpriseIdが存在する場合は法人IDによるwhere条件が追加され、引数が存在しない場合はプライベートプロットを全件取得する。
   */
  async getPrivatePlots(enterpriseId = null) {
    let query = db.collection(COLLECTIONS.GKMG_PRIVATE_PLOTS);
    query = query.where("deleted_at", "==", null);
    // 法人IDによる絞り込み
    if (enterpriseId) {
      query = query.where("enterprise_id", "==", enterpriseId);
    }
    let privatePlots = [];
    await query
      .get()
      .then((docs) => {
        if (docs.empty) privatePlots = [];
        if (!docs.size) privatePlots = [];
        docs.forEach((doc) => {
          const params = {
            ...doc.data(),
            hash: doc.id,
          };
          privatePlots.push(params);
        });
      })
      .catch((err) => {
        Vue.$log.error("Error getting collection", err);
        privatePlots = [];
      });
    return privatePlots;
  }

  /**
   * プライベートプロットの取得
   * 作成日時を指定し、その時点で作成済みのものを取得
   * 削除済みのものも併せて取得
   */
  async getPrivatePlotsCreatedAtTheTime(
    createdAt, // Date
  ) {
    const privatePlots = [];
    await db
      .collection(COLLECTIONS.GKMG_PRIVATE_PLOTS)
      .where(
        "created_at",
        "<=",
        firebase.firestore.Timestamp.fromDate(createdAt),
      )
      .get()
      .then((docs) => {
        docs.forEach((doc) => {
          privatePlots.push({
            ...doc.data(),
            hash: doc.id,
          });
        });
      })
      .catch((err) => {
        Vue.$log.error("Error getting collection", err);
      });
    return privatePlots;
  }

  /**
   * hashに紐づくプライベートプロットを取得する
   */
  async getPrivatePlotByHash(hash) {
    return await this.get(COLLECTIONS.GKMG_PRIVATE_PLOTS, hash);
  }

  /**
   * 拠点IDに紐づく拠点情報を取得する
   */
  async getEquipmentBaseStation(hash) {
    return await this.get(COLLECTIONS.GKMG_EQUIPMENT_BASE_STATIONS, hash);
  }

  /**
   * ドキュメントを取得
   */
  async get(collection, documentPath) {
    if (documentPath) {
      let ref = db.collection(collection).doc(documentPath);
      return ref
        .get()
        .then((doc) => {
          if (!doc.exists) {
            return null;
          } else {
            return doc.data();
          }
        })
        .catch((err) => {
          Vue.$log.error("Error getting document", err);
          return null;
        });
    } else {
      return null;
    }
  }

  /**
   * ドキュメントを作成または上書き
   */
  async set(collection, documentPath, data, isMerge) {
    if (documentPath) {
      return db
        .collection(collection)
        .doc(documentPath)
        .set(data, { merge: isMerge })
        .catch((err) => {
          Vue.$log.error("Error setting document", err);
          return null;
        });
    } else {
      return null;
    }
  }
  /**
   * ==============================
   * グリッドマップ用
   * ==============================
   */
  /** =======グリッドマップ用GETTER====== */
  /** 共通getter */
  async getGridMapSettings(mapId) {
    if (!mapId) return;

    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const collectionRef = db.collection(
      `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/grid_map_settings/`,
    );
    /** 各mapIdに紐づくsettingsは1つしかない想定 */
    const querySnapshot = await collectionRef
      .where("map_id", "==", mapId)
      .limit(1)
      .get();
    const settings = {};
    if (querySnapshot.size !== 0) {
      Object.assign(settings, querySnapshot.docs[0].data());
    }
    const { default_position } = settings;
    if (default_position) {
      default_position.center = [
        default_position.center.longitude,
        default_position.center.latitude,
      ];
    }
    return {
      map_id: mapId,
      default_position: MAP_DEFAULT_POSITION,
      map_style: MAP_STYLE_DEFAULT,
      filter_vessels: VesselMixin.methods.getVesselCategoryIds(),
      filter_plots: PlotMixin.methods.getPlotParentCategoryIds(),
      filter_private_plots:
        PrivatePlotMixin.methods.getPrivatePlotParentCategoryIds(),
      filter_navigations: NavigationsMixin.methods.getNavigationsCategoryIds(),
      selected_favorite_index: MAP_DEFAULT_SELECTED_FAVORITE_INDEX,
      ...settings,
    };
  }

  /** 初期表示位置 */
  async getDefaultPositionForGrid(mapId) {
    if (!mapId) return;
    let settings = await this.getGridMapSettings(mapId);
    if (!settings.default_position) {
      await this.setGridMapSettings(mapId, {
        default_position: MAP_DEFAULT_POSITION,
      });
      settings.default_position = MAP_DEFAULT_POSITION;
    }
    return settings.default_position;
  }

  /** 地図の種類 */
  async getMapStyleForGrid(mapId) {
    if (!mapId) return;
    let settings = await this.getGridMapSettings(mapId);
    if (!settings.map_style) {
      await this.setGridMapSettings(mapId, { map_style: MAP_STYLE_DEFAULT });
      settings.map_style = MAP_DEFAULT_POSITION;
    }
    return settings.map_style;
  }

  /** 船のフィルター */
  async getFilterVesselsForGrid(mapId) {
    if (!mapId) return [];
    let settings = await this.getGridMapSettings(mapId);
    if (!settings.filter_vessels) {
      const vesselCategoryIds = VesselMixin.methods.getVesselCategoryIds();
      await this.setGridMapSettings(mapId, {
        filter_vessels: vesselCategoryIds,
      });
      settings.filter_vessels = vesselCategoryIds || [];
    }
    return settings.filter_vessels;
  }

  /** プロットのフィルター */
  async getFilterPlotsForGrid(mapId) {
    if (!mapId) return [];
    let settings = await this.getGridMapSettings(mapId);
    if (!settings.filter_plots) {
      const plotCategoryIds = PlotMixin.methods.getPlotParentCategoryIds();
      await this.setGridMapSettings(mapId, { filter_plots: plotCategoryIds });
      settings.filter_plots = plotCategoryIds || [];
    }
    return settings.filter_plots;
  }

  /** 航路、標識のフィルター */
  async getFilterNavigationsForGrid(mapId) {
    if (!mapId) return [];
    const settings = await this.getGridMapSettings(mapId);
    return settings.filter_navigations || [];
  }

  /** 選択船のタブインデックス */
  async getSelectedFavoriteIndexForGrid(mapId) {
    if (!mapId) return;
    let settings = await this.getGridMapSettings(mapId);
    if (!settings.selected_favorite_index) {
      await this.setGridMapSettings(mapId, {
        selected_favorite_index: MAP_DEFAULT_SELECTED_FAVORITE_INDEX,
      });
      settings.selected_favorite_index = MAP_DEFAULT_SELECTED_FAVORITE_INDEX;
    }
    return settings.selected_favorite_index;
  }

  /** 追跡設定 */
  async getTrackingTargetForGrid(mapId) {
    if (!mapId) return;
    const settings = await this.getGridMapSettings(mapId);
    return settings.tracking_target;
  }

  /** =======グリッドマップ用SETTER====== */
  // WARNING: データ更新用関数。新規作成には使用できない
  // 新規作成を同時実行時に重複データが作成される可能性があるため、それを避けるために
  // initGridMapSettingsで先に更新するmapIdを持ったデータを作成する
  async setGridMapSettings(mapId, data) {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/grid_map_settings/`;
    try {
      const { default_position, ...settings } = data;
      if (settings && settings.last_zoom) {
        settings.last_zoom = NumberUtil.toTrunc(settings.last_zoom, 2);
      }

      let defaultPosition;
      if (default_position) {
        defaultPosition = {
          default_position: {
            center: new firebase.firestore.GeoPoint(
              NumberUtil.toTrunc(default_position.center[1], 8),
              NumberUtil.toTrunc(default_position.center[0], 8),
            ),
            zoom: NumberUtil.toTrunc(default_position.zoom, 2),
          },
        };
      }
      const collectionRef = db.collection(path);
      /** 各mapIdに紐づくsettingsは1つしかない想定 */
      const querySnapshot = await collectionRef
        .where("map_id", "==", mapId)
        .limit(1)
        .get();
      if (querySnapshot.size !== 0) {
        // 更新対象が存在する場合はupdate
        querySnapshot.docs[0].ref.set(
          { ...defaultPosition, ...settings, map_id: mapId },
          { merge: true },
        );
      } else {
        // 更新対象が存在しない場合はエラー
        Vue.$log.error("Error method:setGridMapSettings", mapId);
      }
    } catch (e) {
      Vue.$log.error("Error method:setGridMapSettings", `${e.message}`);
    }
  }

  // mapIdを持ったデータを新規作成する
  // WARNING: 短時間に連続実行すると重複データが作成される可能性があるため、そのような使用を避ける
  async initGridMapSetting(mapId) {
    const hasValidGridMapSetting = await this.checkGridMapSettingReady(mapId);
    if (hasValidGridMapSetting) return;

    // データがない場合に新規作成
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/grid_map_settings/`;
    const collectionRef = db.collection(path);

    collectionRef.add({ map_id: mapId });
  }

  async checkGridMapSettingReady(mapId) {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/grid_map_settings/`;
    const collectionRef = db.collection(path);
    /** 各mapIdに紐づくsettingsは1つしかない想定 */
    const querySnapshot = await collectionRef
      .where("map_id", "==", mapId)
      .limit(1)
      .get();
    return !querySnapshot.empty;
  }

  /** プライベートプロットSETTER */
  async setPrivatePlot(params) {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.GKMG_PRIVATE_PLOTS}`;
    try {
      const targetPlot = {
        enterprise_id: enterpriseId,
        plot_child_category_id: String(params.child_category.id),
        title: params.title,
        start_date: params.detail.start_date || null,
        end_date: params.detail.end_date || null,
        comment: params.detail.comment || null,
        installer: params.detail.installer || null,
        phone_number: params.detail.phone_number || null,
        points: params.points.map((point) => {
          return new firebase.firestore.GeoPoint(
            NumberUtil.toTrunc(point.latitude, 8),
            NumberUtil.toTrunc(point.longitude, 8),
          );
        }),
        center_point: new firebase.firestore.GeoPoint(
          NumberUtil.toTrunc(params.center_point[1], 8),
          NumberUtil.toTrunc(params.center_point[0], 8),
        ),
        picture: params.picture,
        created_at: params.created_at,
        updated_at: params.updated_at,
        deleted_at: params.deleted_at || null,
        account_id: accountId,
        quadkey: params.quadkey || null,
      };

      if (params.hash) {
        // ハッシュを持つときは更新処理
        await this.set(path, params.hash, targetPlot, true);
        targetPlot.hash = params.hash;
      } else {
        // ハッシュを持たないときは登録処理
        const createdTarget = await db.collection(path).add(targetPlot);
        // 登録後処理のためhashを追加
        targetPlot.hash = createdTarget.id;
      }
      // 更新完了後の処理のために
      return {
        status: 200,
        data: targetPlot,
      };
    } catch (e) {
      Vue.$log.error("Error method:setPrivatePlot", `${e.message}`);
    }
  }

  /** プライベートプロット 削除 */
  async deletePrivatePlot(params) {
    try {
      if (!params.hash) throw new Error("Error method:deletePrivatePlot");

      const accountId = await this.getFirebaseAccountId();
      const path = `${COLLECTIONS.GKMG_PRIVATE_PLOTS}`;
      const targetPlot = {
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        deleted_at: firebase.firestore.FieldValue.serverTimestamp(),
        account_id: accountId,
      };

      await this.set(path, params.hash, targetPlot, true);
      targetPlot.hash = params.hash;
      // 更新完了後の処理のために
      return {
        status: 200,
        data: targetPlot,
      };
    } catch (e) {
      Vue.$log.error("Error method:deletePrivatePlot", `${e.message}`);
    }
  }

  async getEnterpriseAccountPrivilegeDocumentPath() {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    return `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/privileges`;
  }

  /**
   * ==============================
   * 仮設桟橋管理用(Port Master C)
   * ==============================
   */
  async getGkmgDestinationWorkListColumnsSortOrder() {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/gkmg_port_master_work_list/`;
    const collectionRef = db.collection(path);

    const sortOrderList = [];

    const snapshot = await collectionRef.get();
    snapshot.forEach((doc) => {
      sortOrderList.push({
        column_name: doc.data().column_name,
        sort_order: doc.data().sort_order,
        document_id: doc.id,
      });
    });

    return sortOrderList;
  }

  async setGkmgDestinationWorkListColumnsSortOrder(sortOrderList) {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/gkmg_port_master_work_list/`;
    sortOrderList.forEach((col, index) => {
      /*
       * 今現時点では、gkmg_port_master_work_listには列の並び順しか保存していないため、mergeパラメータはfalseでも良いが、
       * 今後他の用途にも使うようになった時に、意図せず保存していた項目を削除しないようにmerge: trueとしている。
       */
      this.set(
        path,
        col.docId,
        {
          column_name: col.columnName,
          sort_order: index,
        },
        true,
      );
    });
  }

  async deleteUnnecessaryColumnsSortOrder(whiteList) {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const accountId = await this.getFirebaseAccountId();
    const path = `${COLLECTIONS.ENTERPRISE_ACCOUNTS}/e:${enterpriseId}/a:${accountId}/settings/gkmg_port_master_work_list/`;
    const collectionRef = db.collection(path);

    const snapshot = await collectionRef.get();
    snapshot.forEach((doc) => {
      if (!whiteList.includes(doc.data().column_name)) {
        collectionRef.doc(doc.id).delete();
      }
    });
  }

  // gkmg用権限情報取得
  async getGkmgPortMasterAuth() {
    const user = await AuthService.getUser();
    const enterpriseId = user.id;
    const userAccountId = await this.getFirebaseAccountId();

    const doc = await db
      .collection("enterprise_accounts")
      .doc(`e:${enterpriseId}`)
      .collection(`a:${userAccountId}`)
      .doc("privileges")
      .collection("gkmg_port_master")
      .doc("port_master")
      .get();

    return doc;
  }

  async getIsConnectedToGkmgPmTasks(documentId) {
    // gkmg_pm_tasksの更新対象の特定のドキュメントrefを指定して、疎通を確認する
    return await db
      .collection(COLLECTIONS.GKMG_PM_TASKS)
      .doc(documentId)
      .get({ source: "server" })
      .then(() => true)
      .catch(() => false);
  }

  /**
   * Firestoreに接続されているかどうか判定する
   *
   * @param {string} collectionPath - コレクションのパス
   * @param {string} documentId - ドキュメントのID
   * @returns {Promise<boolean>} Firestoreに接続されている場合はtrue、接続されていない場合はfalseを返す
   * @throws {Error} collectionPathまたはdocumentIdが無効な場合にエラーがスローされる
   */
  async getIsConnectedToFirestore(
    collectionPath, // string
    documentId, // string
  ) {
    if (!(isString(collectionPath) && collectionPath != "")) {
      throw new Error("collectionPath is invalid");
    }
    if (!(isString(documentId) && documentId != "")) {
      throw new Error("documentId is invalid");
    }
    return await db
      .collection(collectionPath)
      .doc(documentId)
      .get({ source: "server" })
      .then(() => true)
      .catch(() => false);
  }

  /**
   * GKMG PMタスクを削除します。
   * @param {Object} payload - 削除するタスクの情報を含むオブジェクト
   * @param {string} payload.id - 削除するタスクのID
   * @returns {Promise<void>} - 削除が完了するまで解決されないPromiseオブジェクト
   */
  async deleteGkmgPmTask(
    payload, // {id: string;}
  ) {
    const documentId = payload.id;
    const docRef = await db
      .collection(COLLECTIONS.GKMG_PM_TASKS)
      .doc(documentId);
    await docRef.delete(); // 物理削除
  }

  /**
   * GKMG PMタスクを作成します。
   * @param {Object} payload - タスクのデータ
   * @param {Date} payload.order_time - order時間
   * @param {string|null} payload.vessel_id - 船ID
   * @param {string} payload.vessel_name - 船名
   * @param {number} payload.progress - 進捗
   * @returns {Promise<void>}
   */
  async createGkmgPmTask(
    payload, // {order_time: Date; vessel_id: string; vessel_name: string; progress: number}
    //        | {order_time: Date; vessel_id: null; vessel_name: string; progress: number}
  ) {
    const { order_time, vessel_id, vessel_name, progress } = payload;
    const timestamp = firebase.firestore.FieldValue.serverTimestamp();
    const newTaskData = {
      order_time,
      vessel_id,
      vessel_name,
      progress,
      created_at: timestamp,
      updated_at: timestamp,
    };
    const collectionRef = await db.collection(COLLECTIONS.GKMG_PM_TASKS);
    return await collectionRef.add(newTaskData);
  }

  // objがfirebase.firestore.Timestamp型かどうかを判定する
  isInstanceOfFirebaseFirestoreTimestamp(
    obj, // any
  ) {
    return obj instanceof firebase.firestore.Timestamp; // boolean
  }

  async cleanupLocalData() {
    try {
      await firebase.auth().signOut(); // ローカルの firebase のデータ削除
      return true;
    } catch {
      return false;
    }
  }
}

export default new FireStoreService();
